import {
  ActionIcon,
  AppShell,
  Avatar,
  Burger,
  Center,
  Loader,
  Menu,
  ScrollArea,
  Text,
  Tooltip,
  UnstyledButton,
  useComputedColorScheme,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure, useLocalStorage } from '@mantine/hooks';
import {
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconLogout,
  IconUserCircle,
  IconMoon,
  IconSun,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { CommandSearch } from '../components/CommandSearch.js';
import { ImpersonationBanner } from '../components/ImpersonationBanner.js';
import { PageBreadcrumbs } from '../components/PageBreadcrumbs.js';
import { NAV, type NavItem } from './adminNav.js';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthActions } from '../auth/AuthGate.js';
import { api, ApiError, type Me } from '../lib/api.js';
import classes from './AdminLayout.module.css';
import { Logo } from '../components/Logo.js';

const EXPANDED = 248;
const COLLAPSED = 68;

/** Guards every admin route: an unauthenticated visitor is sent to /login. */
export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, { toggle: toggleMobile, close: closeMobile }] = useDisclosure();
  // The stored scheme defaults to 'auto', which never equals 'dark' — reading
  // it directly made the toggle offer "Dark mode" while already dark. The
  // COMPUTED scheme resolves 'auto' against the OS preference.
  const { setColorScheme } = useMantineColorScheme();
  const scheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDark = scheme === 'dark';

  // Persisted so the rail does not spring back open on every page load.
  const [collapsed, setCollapsed] = useLocalStorage({
    key: 'resolve-nav-collapsed',
    defaultValue: false,
    getInitialValueInEffect: false,
  });

  // Via context, never useClerk directly: this component also renders when
  // Clerk is not configured, and the hook throws outside its provider.
  const { signOutProvider } = useAuthActions();

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/admin/me'),
  });

  if (me.isLoading) {
    return (
      <Center mih="100vh">
        <Loader />
      </Center>
    );
  }

  if (me.isError) {
    if ((me.error as ApiError).status === 401) {
      navigate('/login', { replace: true });
      return null;
    }
    return (
      <Center mih="100vh">
        <Text c="red">{(me.error as Error).message}</Text>
      </Center>
    );
  }

  // A dealership account has no business in the console, and half of it would
  // be empty for them anyway. Send them where their data is.
  if (me.data?.kind === 'dealer') {
    return <Navigate to="/portal" replace />;
  }

  async function signOut() {
    // Always clear our own session. On Clerk, also end the Clerk session —
    // otherwise the cookie goes but Clerk signs the user straight back in.
    await api.post('/admin/logout').catch(() => {});
    // No-op on the password provider; ends the Clerk session otherwise.
    // Clearing only our cookie would let Clerk sign the user straight back in.
    await signOutProvider();
    navigate('/login', { replace: true });
  }

  const email = me.data?.email ?? '';
  const initial = email.slice(0, 1).toUpperCase() || '?';

  // Hiding a link the user cannot use is kinder than letting them click into
  // a 403. The server enforces the same rules regardless of what is rendered.
  const held = me.data?.permissions ?? [];
  const visibleNav = NAV.filter((i) => !i.permission || held.includes(i.permission));

  function isActive(item: NavItem) {
    return item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);
  }

  return (
    <AppShell
      // "alt" runs the navbar the FULL height of the viewport and sits the
      // header beside it, rather than the header spanning across the top. The
      // brand then belongs to the rail it labels.
      layout="alt"
      header={{ height: 60 }}
      navbar={{
        width: collapsed ? COLLAPSED : EXPANDED,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpen },
      }}
      padding="lg"
    >
      <AppShell.Header>
        <div className={classes.header}>
          {/* The only other control here, and only on mobile — without it the
              nav would be unreachable below the breakpoint. */}
          <Burger
            opened={mobileOpen}
            onClick={toggleMobile}
            hiddenFrom="sm"
            size="sm"
            aria-label="Toggle navigation"
          />

          {/* Where you are, then how to get anywhere else. */}
          <div className={classes.crumbs}>
            <PageBreadcrumbs />
          </div>

          <div className={classes.search}>
            <CommandSearch permissions={held} />
          </div>

          <Tooltip label={isDark ? 'Light mode' : 'Dark mode'}>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              radius="md"
              onClick={() => setColorScheme(isDark ? 'light' : 'dark')}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <IconSun size={18} stroke={1.7} /> : <IconMoon size={18} stroke={1.7} />}
            </ActionIcon>
          </Tooltip>

          <Menu position="bottom-end" width={220} withArrow>
            <Menu.Target>
              <UnstyledButton className={classes.avatarBtn} aria-label="Account menu">
                <Avatar size={32} radius="xl" color="brand" variant="filled">
                  {initial}
                </Avatar>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>
                <Text size="xs" c="dimmed" truncate>
                  {email}
                </Text>
              </Menu.Label>
              <Menu.Item
                leftSection={<IconUserCircle size={15} />}
                onClick={() => navigate('/app/account')}
              >
                Account settings
              </Menu.Item>
              <Menu.Item
                leftSection={isDark ? <IconSun size={15} /> : <IconMoon size={15} />}
                onClick={() => setColorScheme(isDark ? 'light' : 'dark')}
              >
                {isDark ? 'Light mode' : 'Dark mode'}
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                color="red"
                leftSection={<IconLogout size={15} />}
                onClick={() => void signOut()}
              >
                Sign out
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      </AppShell.Header>

      <AppShell.Navbar
        className={`${classes.navbar} ${collapsed ? classes.collapsed : ''}`}
        withBorder={false}
      >
        <NavLink to="/app" className={classes.brand}>
          {/* The rail is dark in both colour schemes, so the dark-ground artwork always. */}
          {collapsed ? (
            <Logo on="dark" markOnly height={22} />
          ) : (
            <span className={classes.brandText}>
              <Logo on="dark" height={22} />
              <div className={classes.brandSub}>Admin console</div>
            </span>
          )}
        </NavLink>

        <ScrollArea flex={1} px={10} scrollbarSize={6} type="hover">
          {!collapsed && <div className={classes.section}>Manage</div>}
          {visibleNav.map((item) => {
            const active = isActive(item);
            // aria-label is set unconditionally: collapsed, the icon is the
            // only visible content, so the name must live on the element.
            const link = (
              <NavLink
                key={item.to}
                to={item.to}
                className={classes.link}
                data-active={active || undefined}
                aria-current={active ? 'page' : undefined}
                aria-label={item.label}
                onClick={closeMobile}
              >
                <span className={classes.linkIcon}>
                  <item.icon size={19} stroke={1.7} />
                </span>
                {!collapsed && <span className={classes.linkLabel}>{item.label}</span>}
              </NavLink>
            );

            // Collapsed to icons, the label has to come from somewhere.
            return collapsed ? (
              <Tooltip key={item.to} label={item.label} position="right" withArrow offset={10}>
                {link}
              </Tooltip>
            ) : (
              link
            );
          })}
        </ScrollArea>

        <div className={classes.footer}>
          <Tooltip
            label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            position="right"
            withArrow
            offset={10}
          >
            <UnstyledButton
              className={classes.collapseBtn}
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
            >
              {collapsed ? (
                <IconLayoutSidebarLeftExpand size={19} stroke={1.7} />
              ) : (
                <>
                  <IconLayoutSidebarLeftCollapse size={19} stroke={1.7} />
                  <span>Collapse</span>
                </>
              )}
            </UnstyledButton>
          </Tooltip>
        </div>
      </AppShell.Navbar>

      <AppShell.Main>
        {/* Above the page, not inside it, so no route can forget to show it. */}
        {me.data?.impersonating && <ImpersonationBanner target={me.data.impersonating} />}
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
